import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useCreateSettings, useGetSettings } from '../../../hooks';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { yupResolver } from '@hookform/resolvers/yup';
import PlusIcon from '../../../components/atoms/icons/plus-icon';
import { DeleteIcon } from '../../atoms';
import { manageSettingsSchema } from '../../../constants';
import { showError, showSuccess } from '../../../utils';
import ResetIcon from '../../atoms/icons/clear-reset.icon';
import { ScheduleConfirmationModal } from '../../atoms/schedule-confirmation-modal/confirmation-modal';
import { WheelTimePicker } from '../../atoms/time-picker/time-picker';

const convertToProperCase = (dayName) => {
  // Convert the first letter to uppercase and the rest to lowercase
  return dayName.charAt(0).toUpperCase() + dayName.slice(1).toLowerCase();
};

const normalizeTimeString = (val) => {
  if (!val) return '';
  const s = String(val).trim();

  const hhmm = s.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    return `${String(parseInt(hhmm[1], 10)).padStart(2, '0')}:${hhmm[2]}`;
  }

  const embedded = s.match(/(\d{2}):(\d{2})/);
  if (embedded) {
    return `${embedded[1]}:${embedded[2]}`;
  }

  return s;
};

export const BusinessHoursForm = ({
  setScheduleUpdate,
  closeModal,
  scheduleUpdate,
  setIsLoading,
}) => {
  const handleCreateSettings = useCreateSettings();
  const { data, loading } = useGetSettings();

  const [checked, setChecked] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(manageSettingsSchema),
    defaultValues: {
      days: [
        {
          dayName: 'monday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
        {
          dayName: 'tuesday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
        {
          dayName: 'wednesday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
        {
          dayName: 'thursday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
        {
          dayName: 'friday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
        {
          dayName: 'saturday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
        {
          dayName: 'sunday',
          enabled: false,
          time: [
            {
              endTime: '',
              startTime: '',
              indexId: 0,
            },
          ],
        },
      ],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'days',
  });

  const convertData = (data) => {
    let convertedData = [];

    data?.forEach((item) => {
      // Flatten the nested time array
      item.time.forEach((timeEntry) => {
        // Create a new object with the desired format
        // Send time strings directly (e.g., "10:00 AM") - no conversion needed
        const newItem = {
          dayName: item.dayName,
          enabled: item.enabled,
          endTime: normalizeTimeString(timeEntry.endTime),
          startTime: normalizeTimeString(timeEntry.startTime),
        };

        // Add the new object to the converted data array
        convertedData.push(newItem);
      });
    });

    return convertedData;
  };

  const [scheduleConfirmationModal, setScheduleConfirmationModal] =
    useState(false);
  const [scheduleConfirmationModalData, setScheduleConfirmationModalData] =
    useState(false);

  // Define confirmation callback function
  const confirmationCallback = async (confirmed, formData) => {
    if (confirmed) {
      setIsLoading(true);
      // Proceed with form submission
      const convertedData = convertData(formData?.days);
      const payload = {
        input: convertedData,
      };

      const response = await handleCreateSettings(payload);
      if (response?.data?.setBusinessHoursSetting) {
        closeModal();
        setScheduleUpdate(!scheduleUpdate);
        reset();
        setIsLoading(false);
      } else {
        setIsLoading(false);
        showError(response);
      }
    } else {
      // Handle cancellation
      setIsLoading(false);
      closeScheduleConfirmationModal();
      closeModal();
    }
  };

  const onSubmit = async (formData) => {
    // Filter out days where enabled is false
    const filteredData = formData.days.filter((day) => day.enabled);

    if (filteredData.length > 0) {
      if (data && data?.businessHoursSettings?.nodes?.length > 0) {
        // If nodes is not empty, show the confirmation popup
        openScheduleConfirmationModal({ ...formData, days: filteredData }); // Pass the filtered form data to the confirmation modal
      } else {
        // If nodes is empty, skip the confirmation popup and proceed with form submission
        await confirmationCallback(true, { ...formData, days: filteredData });
      }
    } else {
      // Handle the case where all entries are disabled (if needed)
      console.log('No days are enabled.');
    }
  };

  // Open confirmation modal function
  const openScheduleConfirmationModal = (formData) => {
    setScheduleConfirmationModalData(formData); // Store the form data in state
    setScheduleConfirmationModal(true);
  };

  // Close confirmation modal function
  const closeScheduleConfirmationModal = () => {
    setScheduleConfirmationModal(false);
  };

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  const handleChange = (event) => {
    const { checked } = event.target;
    if (checked) {
      const mondaySchedule = fields.find((field) => field.dayName === 'monday');
      if (mondaySchedule) {
        const updatedFields = fields.map((field) => {
          if (field.dayName !== 'monday') {
            // Deep copy the time array to avoid reference issues
            const copiedTime = mondaySchedule.time.map((t) => ({ ...t }));
            return { ...field, enabled: true, time: copiedTime };
          }
          return { ...field, enabled: true };
        });
        setValue('days', updatedFields);
      }
    } else {
      // If the checkbox is unchecked, you may want to revert all other days back to their original state.
      // Here, you would reset the enabled flag to false for all days except Monday.
      const updatedFields = fields.map((field) => {
        if (field.dayName !== 'monday') {
          return { ...field, enabled: false };
        }
        return field;
      });
      setValue('days', updatedFields);
    }
    setChecked(checked);
  };

  useEffect(() => {
    if (data && data?.businessHoursSettings?.nodes) {
      const transformedData = Object.values(
        data?.businessHoursSettings?.nodes.reduce((acc, obj) => {
          const { dayName, enabled, ...rest } = obj;
          if (!acc[dayName]) {
            acc[dayName] = { dayName, enabled, time: [] };
          }
          acc[dayName].time.push({
            ...rest,
            startTime: normalizeTimeString(rest.startTime),
            endTime: normalizeTimeString(rest.endTime),
          });
          return acc;
        }, {})
      );
      // startTime and endTime are now returned as formatted strings from backend
      // No conversion needed
      // Merge transformedData with fields based on dayName
      const mergedFields = fields.map((field) => {
        const match = transformedData.find(
          (data) => data.dayName === field.dayName
        );
        if (match) {
          return { ...field, time: match.time, enabled: match?.enabled };
        }
        return field;
      });

      const allEnabled = transformedData.every((item) => item.enabled === true);
      if (allEnabled) {
        setChecked(true);
      } else {
        setChecked(false);
      }

      // Check if Monday shift has no data, uncheck the checkbox
      const mondaySchedule = transformedData.find(
        (item) => item.dayName === 'monday'
      );
      if (
        !mondaySchedule ||
        (mondaySchedule && mondaySchedule.time.length === 0)
      ) {
        const updatedFields = mergedFields.map((field) => {
          if (field.dayName !== 'monday') {
            return { ...field, enabled: false };
          }
          return field;
        });
        setValue('days', updatedFields);
        setChecked(false);
      } else {
        setValue('days', mergedFields);
      }
    }
  }, [data]);

  const handleAddTimeField = (dayName) => {
    const newTimeField = { endTime: '', startTime: '', indexId: '' };
    const updatedFields = [...fields];
    const dayIndex = updatedFields.findIndex(
      (field) => field.dayName === dayName
    );
    if (dayIndex !== -1) {
      updatedFields[dayIndex].time.push(newTimeField);
      setValue('days', updatedFields);
    }
  };

  const handleDeleteTimeField = (dayName, timeIndex) => {
    const updatedFields = [...fields];
    const dayIndex = updatedFields.findIndex(
      (field) => field.dayName === dayName
    );
    if (dayIndex !== -1) {
      updatedFields[dayIndex].time.splice(timeIndex, 1); // Remove the time field
      setValue('days', updatedFields);
    }
  };

  const handleClearField = (dayName) => {
    const updatedFields = [...fields];
    const dayIndex = updatedFields.findIndex(
      (field) => field.dayName === dayName
    );
    if (dayIndex !== -1 && updatedFields[dayIndex].time.length > 0) {
      updatedFields[dayIndex].time[0] = {
        endTime: '',
        startTime: '',
        indexId: '',
      };
      setValue('days', updatedFields);
    }
  };

  const handleChangeCheckBox = (value, data) => {
    const newData = {
      ...data,
      enabled: value,
    };

    setValue(
      'days',
      fields.map((field) => {
        return field.dayName === data.dayName ? newData : field;
      })
    );
  };

  return (
    <>
      <form className="mt-4 coustom-lable" onSubmit={handleSubmit(onSubmit)}>
        {fields?.length && (
          <>
            {fields.map((data, index) => (
              <>
                <div
                  className="grid grid-cols-1 md:grid-cols-c4 md:gap-2  mt-4"
                  key={data.id}
                >
                  <Controller
                    name={`days.${index}.enabled`} // Unique name for checkbox within days array
                    control={control}
                    defaultChecked={data.enabled}
                    render={({ field }) => (
                      <div className="col-span-3 md:col-auto">
                        <FormControlLabel
                          className="text-base text-gray-dark"
                          control={
                            <Checkbox
                              {...field}
                              color="primary"
                              defaultChecked={data.enabled}
                              onChange={(e) =>
                                handleChangeCheckBox(e.target.checked, data)
                              }
                            />
                          }
                          label={convertToProperCase(data.dayName)}
                        />
                      </div>
                    )}
                  />
                  <div className="w-full">
                    {data.time.map((timeItem, timeIndex) => (
                      <div
                        key={timeIndex}
                        className="grid grid-cols-gAuto md:gap-2 gap-2 mb-4"
                      >
                        <div>
                          <Controller
                            name={`days.${index}.time[${timeIndex}].startTime`}
                            control={control}
                            defaultValue={timeItem.startTime}
                            render={({ field }) => (
                              <WheelTimePicker
                                value={field.value}
                                onChange={(val) => {
                                  field.onChange(val);
                                }}
                                placeholder="Start Time"
                              />
                            )}
                          />
                          {errors?.days?.[index]?.time?.[timeIndex]
                            ?.startTime && (
                            <p className="text-red-drk text-r0.6875">
                              {
                                errors.days[index].time[timeIndex].startTime
                                  .message
                              }
                            </p>
                          )}
                        </div>
                        <div>
                          <Controller
                            name={`days.${index}.time[${timeIndex}].endTime`}
                            control={control}
                            defaultValue={timeItem.endTime}
                            render={({ field }) => (
                              <WheelTimePicker
                                value={field.value}
                                onChange={(val) => {
                                  field.onChange(val);
                                }}
                                placeholder="End Time"
                              />
                            )}
                          />
                          {errors?.days?.[index]?.time?.[timeIndex]
                            ?.endTime && (
                            <p className="text-red-drk text-r0.6875">
                              {
                                errors.days[index].time[timeIndex].endTime
                                  .message
                              }
                            </p>
                          )}
                        </div>
                        {timeIndex > 0 ? (
                          <div
                            className=" flex justify-center items-end pb-4 cursor-pointer"
                            onClick={() =>
                              handleDeleteTimeField(data?.dayName, timeIndex)
                            }
                          >
                            <DeleteIcon />
                          </div>
                        ) : (
                          <>
                            <div className="action-section flex gap-2">
                              <div
                                className=" flex justify-center items-end pb-4 cursor-pointer"
                                onClick={() =>
                                  handleAddTimeField(data?.dayName, index)
                                }
                              >
                                <PlusIcon />
                              </div>
                              <div
                                className=" flex justify-center items-end cursor-pointer"
                                onClick={() =>
                                  handleClearField(data?.dayName, 0)
                                }
                              >
                                <ResetIcon />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {index === 0 && (
                  <div className="flex bg-blue-light mt-4 px-1 py-0">
                    <FormControlLabel
                      className=" text-xs text-gray-dark"
                      control={
                        <Checkbox
                          color="primary"
                          checked={checked}
                          onChange={handleChange}
                        />
                      }
                      label="Copy these working hours to all days"
                    />
                  </div>
                )}
              </>
            ))}
          </>
        )}
        <button className=" w-full p-2 mt-4 rounded bg-primary text-white-main hover:bg-blue-hover">
          Save
        </button>
      </form>
      <ScheduleConfirmationModal
        apptCount=""
        scheduleConfirmationModal={scheduleConfirmationModal}
        closeScheduleConfirmationModal={closeScheduleConfirmationModal}
        confirmationCallback={
          (confirmed) =>
            confirmationCallback(confirmed, scheduleConfirmationModalData) // Pass the stored form data to the confirmation callback
        }
        actionType=""
      />
    </>
  );
};
